import {HttpErrorResponse} from '@angular/common/http';
import {Action} from '@ngrx/store';
import {
  ChallengeView,
  Project,
  ProjectAlert,
  ProjectView,
  ProjectViewRepeatingTaskHistoryDetailParams,
  ProjectViewRoutineDetail,
  RepeatingTaskHistoryItem,
  RemoveUserConflictResponse,
} from '../../model/project';
import {ProjectTask, TaskTemplate} from '../../model/task';

export enum ProjectsActionType {
  GET = '[Projects] Get',
  GET_SUCCESS = '[Projects] Get :: Success',

  GET_SUMMARY = '[Projects] Get Summary',
  GET_SUMMARY_SUCCESS = '[Projects] Get Summary :: Success',

  CREATE = '[Projects] Create',
  CREATE_SUCCESS = '[Projects] Create :: Success',

  PATCH = '[Projects] Patch',
  PATCH_SUCCESS = '[Projects] Patch :: Success',

  PUT = '[Projects] Update',

  UPDATE_SUCCESS = '[Projects] Update :: Success',

  DUPLICATE = '[Projects] Duplicate',
  DUPLICATE_SUCCESS = '[Projects] Duplicate :: Success',

  DELETE = '[Projects] Delete',
  DELETE_SUCCESS = '[Projects] Delete :: Success',

  ADD_PROJECT_TASK = '[Projects] Add Project Task',
  UPDATE_PROJECT_TASK = '[Projects] Update Project Task',
  MOVE_PROJECT_TASK = '[Projects] Move Project Task',
  DELETE_PROJECT_TASK = '[Projects] Delete Project Task',

  GET_PROJECT_VIEW = '[Projects] Get Project View',
  GET_PROJECT_VIEW_SUCCESS = '[Projects] Get Project View :: Success',

  UPDATE_PROJECT_VIEW_TASK_ID = '[Projects] Update Project View Task Id :: Success',

  GET_REPEATING_TASK_HISTORY = '[Projects] Get Repeating Task History',

  UPDATE_ROUTINE_DETAIL_PARAMS = '[Projects]  Update Routine Detail Params',
  UPDATE_PROJECT_VIEW_TOKEN_PARAMS = '[Projects] Update Project View Token Params',

  CREATE_STEP = '[Projects] Create Step',
  UPDATE_STEP = '[Projects] Update Step',
  DELETE_STEP = '[Projects] Delete Step',

  GET_TASK_TEMPLATES = '[Projects] Get Task Templates',
  GET_TASK_TEMPLATES_SUCCESS = '[Projects] Get Task Templates :: Success',

  CREATE_TASK_TEMPLATE = '[Projects] Create Task Template',
  UPDATE_TASK_TEMPLATE = '[Projects] Update Task Template',
  DELETE_TASK_TEMPLATE = '[Projects] Delete Task Template',

  UPDATE_TWD_SUBTASK_VIEW = '[Projects] Update Subtask Metric View',

  CREATE_ALERT = '[Projects] Create Alert',
  UPDATE_ALERT = '[Projects] Update Alert',
  ACTIVATE_ALERT = '[Projects] Activate Alert',
  DEACTIVATE_ALERT = '[Projects] Deactivate Alert',
  DELETE_ALERT = '[Projects] Delete Alert',

  ASSIGN_USER_OR_TEAM = '[Projects] Assign User Or Team',
  REMOVE_USER_OR_TEAM = '[Projects] Remove User Or Team',

  UPDATE_REPEATING_TASK_HISTORY_MAP = '[Projects] Update Repeating Task History Map',

  UPDATE_SCROLL_POSITION = '[Projects] Update Scroll Position',

  UPDATE_CHALLENGE_NEXT_VALID = '[Projects] Update Challenge Next Valid',

  UPDATE_CURRENT_PROJECTS_ROUTE = '[Projects] Update Current Projects Route',

  GET_PROJECT_CHALLENGE = '[Projects] Get Project Challenge',
  GET_PROJECT_CHALLENGE_SUCCESS = '[Projects] Get Project Challenge :: Success',

  UPDATE_CHALLENGE_CYCLE_SUCCESS_THRESHOLD = '[Projects] Update Challenge Cycle Success Threshold',
  UPDATE_CHALLENGE_CYCLE_SUCCESS_THRESHOLD_SUCCESS = '[Projects] Update Challenge Cycle Success Threshold :: Success',

  CLEAR = '[Projects] Clear',
}

export class GetProjectsAction implements Action {
  public readonly type = ProjectsActionType.GET;

  public constructor(
    public payload: {
      onSuccess?: (projects: Project[]) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class GetProjectsSuccessAction implements Action {
  public readonly type = ProjectsActionType.GET_SUCCESS;

  public constructor(public payload: {projects: Project[]}) {}
}

export class GetProjectsSummaryAction implements Action {
  public readonly type = ProjectsActionType.GET_SUMMARY;

  public constructor(
    public payload: {
      onSuccess?: (projects: Partial<Project>[]) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class GetProjectsSummarySuccessAction implements Action {
  public readonly type = ProjectsActionType.GET_SUMMARY_SUCCESS;

  public constructor(public payload: {projectsSummary: Partial<Project>[]}) {}
}

export class CreateProjectAction implements Action {
  public readonly type = ProjectsActionType.CREATE;

  public constructor(
    public payload: {
      project: Project;
      onSuccess?: (project: Project) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class CreateProjectSuccessAction implements Action {
  public readonly type = ProjectsActionType.CREATE_SUCCESS;

  public constructor(public payload: {project: Project}) {}
}

export class PutProjectAction implements Action {
  public readonly type = ProjectsActionType.PUT;

  public constructor(
    public payload: {
      project: Project;
      onSuccess?: (project: Project) => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class PatchProjectAction implements Action {
  public readonly type = ProjectsActionType.PATCH;

  public constructor(
    public payload: {
      project: Project;
      onSuccess?: (project: Project) => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class UpdateProjectSuccessAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_SUCCESS;

  public constructor(public payload: {project: Project}) {}
}

export class DuplicateProjectAction implements Action {
  public readonly type = ProjectsActionType.DUPLICATE;

  public constructor(
    public payload: {
      id: string;
      onSuccess?: (project: Project) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class DuplicateProjectSuccessAction implements Action {
  public readonly type = ProjectsActionType.DUPLICATE_SUCCESS;

  public constructor(public payload: {project: Project}) {}
}

export class DeleteProjectAction implements Action {
  public readonly type = ProjectsActionType.DELETE;

  public constructor(
    public payload: {
      id: string;
      onSuccess?: () => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class DeleteProjectSuccessAction implements Action {
  public readonly type = ProjectsActionType.DELETE_SUCCESS;
}

export class AddProjectTaskAction implements Action {
  public readonly type = ProjectsActionType.ADD_PROJECT_TASK;

  public constructor(
    public payload: {
      projectId: string;
      stepId: string;
      taskIndex: number; // index to insert new task
      task?: ProjectTask;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class UpdateProjectTaskAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_PROJECT_TASK;

  public constructor(
    public payload: {
      projectId: string;
      stepId: string;
      task: ProjectTask;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

//This moves task fro m one step to another
export class MoveProjectTaskAction implements Action {
  public readonly type = ProjectsActionType.MOVE_PROJECT_TASK;

  public constructor(
    public payload: {
      projectId: string;
      stepId: string;
      taskIndex: number; // index to insert new task
      task?: ProjectTask;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class DeleteProjectTaskAction implements Action {
  public readonly type = ProjectsActionType.DELETE_PROJECT_TASK;

  public constructor(
    public payload: {
      projectId: string;
      taskInstanceId: string;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class GetProjectViewAction implements Action {
  public readonly type = ProjectsActionType.GET_PROJECT_VIEW;

  public constructor(
    public payload: {
      projectId: string;
      params: Record<string, string>;
      onSuccess?: (projectView: ProjectView) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class GetProjectViewSuccessAction implements Action {
  public readonly type = ProjectsActionType.GET_PROJECT_VIEW_SUCCESS;

  public constructor(public payload: {projectView: ProjectView}) {}
}

export class UpdateProjectViewTaskId implements Action {
  public readonly type = ProjectsActionType.UPDATE_PROJECT_VIEW_TASK_ID;

  public constructor(public payload: {id: string}) {}
}

export class GetProjectViewRepeatingTaskHistoryAction implements Action {
  public readonly type = ProjectsActionType.GET_REPEATING_TASK_HISTORY;

  public constructor(
    public payload: {
      taskInstanceId: string;
      params: ProjectViewRepeatingTaskHistoryDetailParams;
      onSuccess?: (routineDetail: ProjectViewRoutineDetail) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class UpdateRoutineDetailParamsAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_ROUTINE_DETAIL_PARAMS;

  public constructor(
    public payload: {
      params: ProjectViewRepeatingTaskHistoryDetailParams;
    }
  ) {}
}

export class UpdateProjectViewTokenParamsAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_PROJECT_VIEW_TOKEN_PARAMS;

  public constructor(
    public payload: {
      params: Record<string, string>;
    }
  ) {}
}

export class CreateProjectStepAction implements Action {
  public readonly type = ProjectsActionType.CREATE_STEP;

  public constructor(
    public payload: {
      // this will change
      project: Project;
      onSuccess?: (taskTemplates: TaskTemplate[]) => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class UpdateProjectStepAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_STEP;

  public constructor(
    public payload: {
      // this will change
      project: Project;
      onSuccess?: (taskTemplates: TaskTemplate[]) => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class DeleteProjectStepAction implements Action {
  public readonly type = ProjectsActionType.DELETE_STEP;

  public constructor(
    public payload: {
      // this will change
      project: Project;
      onSuccess?: (taskTemplates: TaskTemplate[]) => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class GetTaskTemplatesAction implements Action {
  public readonly type = ProjectsActionType.GET_TASK_TEMPLATES;

  public constructor(
    public payload: {
      search?: string;
      onSuccess?: (taskTemplates: TaskTemplate[]) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class GetTaskTemplatesSuccessAction implements Action {
  public readonly type = ProjectsActionType.GET_TASK_TEMPLATES_SUCCESS;

  public constructor(public payload: {taskTemplates: TaskTemplate[]}) {}
}

export class CreateTaskTemplateAction implements Action {
  public readonly type = ProjectsActionType.CREATE_TASK_TEMPLATE;
  public constructor(
    public payload: {
      task: TaskTemplate;
      onSuccess?: (task: TaskTemplate) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class UpdateTaskTemplateAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_TASK_TEMPLATE;
  public constructor(
    public payload: {
      task: TaskTemplate;
      onSuccess?: (task: TaskTemplate) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class DeleteTaskTemplateAction implements Action {
  public readonly type = ProjectsActionType.DELETE_TASK_TEMPLATE;

  public constructor(
    public payload: {
      id: string;
      onSuccess?: () => void;
      onFailure?: (err: Error) => void;
    }
  ) {}
}

export class UpdateTwdSubtaskViewAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_TWD_SUBTASK_VIEW;

  public constructor(public payload: {view: string}) {}
}

export class CreateAlertAction implements Action {
  public readonly type = ProjectsActionType.CREATE_ALERT;

  public constructor(
    public payload: {
      alert: ProjectAlert;
      stepId: string;
      projectId: string;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class UpdateAlertAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_ALERT;

  public constructor(
    public payload: {
      alert: ProjectAlert;
      stepId: string;
      projectId: string;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class DeleteAlertAction implements Action {
  public readonly type = ProjectsActionType.DELETE_ALERT;

  public constructor(
    public payload: {
      alertId: string;
      projectId: string;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class AssignUserOrTeamToProjectAction implements Action {
  public readonly type = ProjectsActionType.ASSIGN_USER_OR_TEAM;

  public constructor(
    public payload: {
      projectId: string;
      userId?: string;
      teamId?: string;
      onSuccess?: (project: Project) => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class RemoveUserOrTeamFromProjectAction implements Action {
  public readonly type = ProjectsActionType.REMOVE_USER_OR_TEAM;

  public constructor(
    public payload: {
      projectId: string;
      userId?: string;
      teamId?: string;
      force?: boolean;
      onSuccess?: (project: Project) => void;
      onFailure?: (error: HttpErrorResponse) => void;
      onConflict?: (conflictResponse: RemoveUserConflictResponse) => void;
    }
  ) {}
}

export class UpdateRepeatingTaskHistoryMapAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_REPEATING_TASK_HISTORY_MAP;
  public constructor(public payload: RepeatingTaskHistoryItem) {}
}

export class UpdateScrollPositionAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_SCROLL_POSITION;
  public constructor(public payload: {scrollPosition: number}) {}
}

export class ClearProjectsAction implements Action {
  public readonly type = ProjectsActionType.CLEAR;
}

export class UpdateChallengeNextValidAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_CHALLENGE_NEXT_VALID;
  public constructor(public payload: {valid: boolean}) {}
}

export class GetProjectChallengeAction implements Action {
  public readonly type = ProjectsActionType.GET_PROJECT_CHALLENGE;

  public constructor(
    public payload: {
      projectId: string;
      onSuccess?: (challengeView: ChallengeView) => void;
      onFailure?: (error: Error) => void;
    }
  ) {}
}

export class GetProjectChallengeSuccessAction implements Action {
  public readonly type = ProjectsActionType.GET_PROJECT_CHALLENGE_SUCCESS;

  public constructor(public payload: {projectId: string; challengeView: ChallengeView}) {}
}

export class UpdateCurrentProjectsRouteAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_CURRENT_PROJECTS_ROUTE;
  public constructor(public payload: {route: string}) {}
}

export class UpdateChallengeCycleSuccessThresholdAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_CHALLENGE_CYCLE_SUCCESS_THRESHOLD;

  public constructor(
    public payload: {
      projectId: string;
      cycleNumber: number;
      cycleSuccessThreshold: number;
      onSuccess?: () => void;
      onFailure?: (error: HttpErrorResponse) => void;
    }
  ) {}
}

export class UpdateChallengeCycleSuccessThresholdSuccessAction implements Action {
  public readonly type = ProjectsActionType.UPDATE_CHALLENGE_CYCLE_SUCCESS_THRESHOLD_SUCCESS;

  public constructor(public payload: {projectId: string; cycleNumber: number; cycleSuccessThreshold: number}) {}
}

export type ProjectsAction =
  | GetProjectsAction
  | GetProjectsSuccessAction
  | GetProjectsSummaryAction
  | GetProjectsSummarySuccessAction
  | CreateProjectAction
  | CreateProjectSuccessAction
  | PutProjectAction
  | PatchProjectAction
  | UpdateProjectSuccessAction
  | DuplicateProjectAction
  | DuplicateProjectSuccessAction
  | DeleteProjectAction
  | DeleteProjectSuccessAction
  | AddProjectTaskAction
  | UpdateProjectTaskAction
  | MoveProjectTaskAction
  | DeleteProjectTaskAction
  | GetProjectViewAction
  | GetProjectViewSuccessAction
  | UpdateProjectViewTaskId
  | GetProjectViewRepeatingTaskHistoryAction
  | UpdateRoutineDetailParamsAction
  | UpdateProjectViewTokenParamsAction
  | CreateProjectStepAction
  | UpdateProjectStepAction
  | DeleteProjectStepAction
  | GetTaskTemplatesAction
  | GetTaskTemplatesSuccessAction
  | CreateTaskTemplateAction
  | DeleteTaskTemplateAction
  | UpdateTwdSubtaskViewAction
  | CreateAlertAction
  | UpdateAlertAction
  | DeleteAlertAction
  | AssignUserOrTeamToProjectAction
  | RemoveUserOrTeamFromProjectAction
  | UpdateRepeatingTaskHistoryMapAction
  | UpdateScrollPositionAction
  | UpdateChallengeNextValidAction
  | UpdateCurrentProjectsRouteAction
  | GetProjectChallengeAction
  | GetProjectChallengeSuccessAction
  | UpdateChallengeCycleSuccessThresholdAction
  | UpdateChallengeCycleSuccessThresholdSuccessAction
  | ClearProjectsAction;
